<script setup>
	import { onMounted, onUnmounted, ref } from "vue";
	import { DsIconButton, DsText } from "@coloplast/design-system";

	const props = defineProps({
		headline: {
			type: String,
			required: true
		},
		description: {
			type: String,
			required: true
		},
		timeout: {
			type: String,
			required: true,
			default: "0"
		},
	});


	const show = ref(false);
	const startTime = ref(null);
	const duration = ref(props.timeout);
	const progress = ref(0);

	let animationTimerId;

	// mounted
	onMounted(() => {
		// show toaster
		show.value = true;

		// if timeout is bigger than 0 or not null then call animation
		if (duration.value !== "0") {

			// start timer after 1 second (css animation need to be done - before we start the border animation)
			animationTimerId = setTimeout(() => {
				startTime.value = Date.now();
				animateProgress();
			}, 1000);
		}
	});

	onUnmounted(() => {
		clearTimeout(animationTimerId);
	})

	// methods
	function closeToaster() {
		show.value = false;
	}

	function animateProgress() {
		const currentTime = Date.now();
		const elapsedTime = currentTime - startTime.value;
		progress.value = (elapsedTime / duration.value) * 100;

		if (elapsedTime < duration.value) {
			requestAnimationFrame(animateProgress);
		} else {
			closeToaster();
		}
	}

</script>

<template>
	<div
		v-if="headline || description"
		class="c-toaster--fixed"
	>
		<transition name="toaster"
					enter-from-class="toaster-enter-from"
					enter-to-class="toaster-enter-to"
					leave-from-class="toaster-leave-from"
					leave-to-class="toaster-leave-to">
			<aside v-if="show" class="c-toaster" data-testid="toaster">
				<DsIconButton
							  icon-name="close"
							  variant="ghost-neutral"
							  size="sm"
							  class="c-toaster__close"
							  :class="{'--no-progress': progress == 0 }"
							  :click-handler="() => {closeToaster()}"
							  />
				<div class="c-toaster__content">
					<DsText text-type="body-md" text-style="medium">{{ headline }}</DsText>
					<DsText html-element="div"
							text-type="body-sm"
							text-style="regular"
							class="c-toaster__content-description">
							<div v-html="description"></div>
					</DsText>
				</div>
				<div class="c-toaster__progress-bar" :style="{ width: progress + '%' }"></div>
			</aside>
		</transition>
	</div>
</template>


<style lang="scss" scoped>
	@import "shared/styles/settings/settings";
	@import "shared/styles/tools/tools";

	.c-toaster {
		position: relative;
		width: 100%;
		max-width: 295px;
		height: auto;
		background-color: ds-color("bg", "neutral-subtle");
		box-shadow: ds-shadow("raised");
		padding: 16px;
		border-bottom-left-radius: 4px;
		border-bottom-right-radius: 4px;
		margin-left: ds-space(8);

		@media (min-width: ds-breakpoint('2xl')) {
			margin-left: calc(50vw - ds-container-max("2xl")/2);
		}

		@include t-respond-to-max(medium) {
			max-width: calc(100% - ds-space(11));
		}

		&__content {
			@media (min-width: ds-breakpoint("sm")) {
				max-width: calc(100% - 50px);
			}

			.--no-progress + & {
				@media (max-width: ds-breakpoint("sm")) {
					max-width: calc(100% - 34px);
				}
			}

			&-description {
				color: ds-color("fg", "muted");
				margin-top: 4px;
			}
		}

		@include t-respond-to-min(medium) {
			max-width: 516px;
		}

		&--fixed {
			position: fixed;
			left: 50%;
			width: 100%;
			transform: translateX(-50%);
			top: 140px;
			z-index: 100;

			@include t-respond-to-max(medium) {
				top: 68px;
			}
		}

		&__progress-bar {
			position: absolute;
			bottom: 0;
			left: 0;
			height: 4px;
			background-color: ds-color("bg", "brand");
			border-bottom-left-radius: 4px;
			border-bottom-right-radius: 4px;
		}
	}
</style>

<style lang="scss">
	@import "shared/styles/tools/tools";
	.c-toaster {
		&__close {
			position: absolute;
			top: 16px;
			right: 16px;

			@media (max-width: ds-breakpoint("sm")) {
				&:not(.--no-progress) {
					display: none;
				}
			}
		}
	}

	.toaster-enter-active {
		transition: transform 0.8s ease, opacity 0.4s ease;
		transform: translateY(100%);
	}

	.toaster-leave-active {
		transition: transform 0.8s ease, opacity 0.4s ease;
	}

	.toaster-leave-to,
	.toaster-enter-from {
		opacity: 0;
		transform: translateY(100%);
	}

	.toaster-enter-to,
	.toaster-leave-from {
		opacity: 1;
		transform: translateY(0);
	}
</style>
